"use client";

import { useCallback } from "react";
import { BackendTextareaWithLabel } from "../../backend-textarea";
import type { BackendTextareaWithLabelProps } from "../../backend-textarea";
import {
  useController,
  useFormContext,
  type Control,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";

// Spezielle Textarea Input Komponente für Array von Strings
export function ControlledTextareaArrayInput<
  TFieldValues extends FieldValues = FieldValues,
>({
  name,
  control,
  onChange,
  onBlur,
  onFieldBlur,
  ...props
}: Omit<
  BackendTextareaWithLabelProps,
  "value" | "onChange" | "onBlur" | "error" | "name"
> & {
  name: FieldPath<TFieldValues>;
  control?: Control<TFieldValues>;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  onFieldBlur?: (fieldName: string) => void;
}) {
  const formContext = useFormContext<TFieldValues>();
  const actualControl = control || formContext?.control;

  if (!actualControl) {
    throw new Error(
      "Controlled textarea must be used inside FormProvider or with control prop"
    );
  }

  const {
    field,
    fieldState: { error },
  } = useController({ name, control: actualControl });

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const raw = e.target.value;
      // Split by lines but DON'T trim - allow spaces while typing
      const lines = raw.split("\n");
      field.onChange(lines);
      onChange?.(e);
    },
    [field, onChange]
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      // Clean up empty lines on blur but keep content with spaces
      if (Array.isArray(field.value)) {
        const cleanedArray = field.value.filter(
          (line: string) => line.length > 0
        );
        field.onChange(cleanedArray);
      }
      field.onBlur();
      onBlur?.(e);
      onFieldBlur?.(name);
    },
    [field, onBlur, onFieldBlur, name]
  );

  // Display array as lines
  const displayValue = Array.isArray(field.value) ? field.value.join("\n") : "";

  return (
    <BackendTextareaWithLabel
      {...props}
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      error={error?.message}
      readOnly={props.readOnly}
    />
  );
}
